import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient } from '@qwickforge/react-client';
import { Text } from '@qwickapps/react-framework';

interface Project {
  id: string;
  name: string;
}

interface Device {
  id: string;
  name: string;
  hostname?: string;
  deletedAt?: string | null;
}

interface DbSession {
  id: string;
  deviceId: string;
  orgId: string;
  userId: string;
  projectId: string | null;
  title: string | null;
  startedAt: Date | string;
  endedAt: Date | string | null;
  inactiveClosed: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  metadata: Record<string, unknown>;
}

interface SessionRow extends DbSession {
  promptCount?: number;
  durationMs?: number;
}

function formatDuration(ms: number | undefined): string {
  if (ms === undefined) return 'Running';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

function truncateId(id: string): string {
  return id.slice(0, 8) + '…';
}

export default function SessionsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const client = useMiddlewareClient();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadData();
  }, [session?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!client || !session?.accessToken) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const [sessionsResult, projectsResult, devicesResult] = await Promise.all([
        client.sessions.list(undefined, 100),
        client.projects.list(),
        client.devices.list(),
      ]);

      const sessionData: DbSession[] = sessionsResult.data || [];
      const projectsData: Project[] = projectsResult || [];
      const devicesRaw = Array.isArray(devicesResult) ? devicesResult : (devicesResult as any).devices || [];
      const devicesData: Device[] = devicesRaw.filter((d: Device) => !d.deletedAt);

      setProjects(projectsData);
      setDevices(devicesData);

      if (sessionData.length === 0) {
        setSessions([]);
        return;
      }

      // Enrich with stats
      let statsMap: Map<string, { promptCount: number }> = new Map();
      try {
        statsMap = await client.sessions.getStats(sessionData.map((s) => s.id)) as Map<string, { promptCount: number }>;
      } catch {
        // stats are optional; proceed without them
      }

      const enriched: SessionRow[] = sessionData.map((s) => {
        const startMs = new Date(s.startedAt as unknown as string).getTime();
        const endMs = s.endedAt ? new Date(s.endedAt as unknown as string).getTime() : undefined;
        return {
          ...s,
          promptCount: statsMap.get(s.id)?.promptCount,
          durationMs: endMs !== undefined ? endMs - startMs : undefined,
        };
      });

      setSessions(enriched);
    } catch {
      setError('Failed to load sessions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const projectMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const deviceMap = Object.fromEntries(
    devices.map((d) => [d.id, d.name || d.hostname || truncateId(d.id)])
  );

  const filtered = sessions.filter((s) => {
    if (selectedProjectId && s.projectId !== selectedProjectId) return false;
    if (selectedDeviceId && s.deviceId !== selectedDeviceId) return false;
    const startedAt = new Date(s.startedAt as unknown as string);
    if (dateFrom && startedAt < new Date(dateFrom)) return false;
    if (dateTo && startedAt > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Text variant="h4" content="Sessions" gutterBottom />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Toolbar
        disableGutters
        sx={{ gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="project-filter-label">Project</InputLabel>
          <Select
            labelId="project-filter-label"
            value={selectedProjectId}
            label="Project"
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="device-filter-label">Device</InputLabel>
          <Select
            labelId="device-filter-label"
            value={selectedDeviceId}
            label="Device"
            onChange={(e) => setSelectedDeviceId(e.target.value)}
          >
            <MenuItem value="">All Devices</MenuItem>
            {devices.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {deviceMap[d.id]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="From"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          size="small"
          label="To"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Toolbar>

      {/* Table */}
      {filtered.length === 0 ? (
        <Box
          data-testid="sessions-empty"
          sx={{ py: 8, textAlign: 'center' }}
        >
          <Text
            variant="body1"
            content="No sessions found"
            customColor="var(--theme-text-secondary)"
          />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" data-testid="sessions-table">
            <TableHead>
              <TableRow>
                <TableCell>Session ID</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Prompts</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/dashboard?session=${s.id}`)}
                  data-testid={`session-row-${s.id}`}
                >
                  <TableCell
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    {truncateId(s.id)}
                  </TableCell>
                  <TableCell>
                    {s.projectId ? (projectMap[s.projectId] ?? truncateId(s.projectId)) : '—'}
                  </TableCell>
                  <TableCell>
                    {deviceMap[s.deviceId] ?? truncateId(s.deviceId)}
                  </TableCell>
                  <TableCell>
                    {new Date(s.startedAt as unknown as string).toLocaleString()}
                  </TableCell>
                  <TableCell>{formatDuration(s.durationMs)}</TableCell>
                  <TableCell>{s.promptCount ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.endedAt ? 'Closed' : 'Running'}
                      size="small"
                      color={s.endedAt ? 'default' : 'success'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
